import { api } from '@/api'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { compareLoose, valid } from 'semver'
import { WorkerType } from './models'
import { useFetchWorkers } from './useFetchWorkers'

type ConfigurationType = {
  min_extension_version: string
  min_android_app_version: string
  job_execution_delay: number
  reload_extension_after_jobs: number
  referral_activation_hours: number
  referral_activation_referrer_points: number
  extension_installation_reward_points: number
}

export const useCurrentConfiguration = () => {
  return useQuery({
    queryKey: ['CONFIG_CURRENT_VERSION'],
    queryFn: (): Promise<ConfigurationType> => api.get(`/configuration`),
    staleTime: 60_000,
    refetchOnWindowFocus: 'always'
  })
}

export const isVersionOutdated = ({
  version,
  type,
  lastTimeActive,
  config
}: {
  version: string
  lastTimeActive: string
  type: WorkerType
  config: ConfigurationType
}) => {
  const inputDate = dayjs(lastTimeActive)
  const twoWeeksAgo = dayjs().subtract(2, 'week')
  if (inputDate.isAfter(twoWeeksAgo)) {
    return valid(version)
      ? compareLoose(version, type === 'mobile' ? config.min_android_app_version : config.min_extension_version) === -1
      : true
  }
  return false
}

export const useHasAnyOutdatedDevice = () => {
  const { data } = useCurrentConfiguration()
  const { data: workers } = useFetchWorkers()

  const isAnyWorkerOutdated = useMemo(
    () =>
      data &&
      workers?.some((worker) => {
        if (worker.metadata) {
          return isVersionOutdated({
            version: worker.metadata.clientVersion,
            lastTimeActive: worker.metadata.lastTimeActive,
            type: worker.type,
            config: data
          })
        }
        return true
      }),
    [data, workers]
  )

  return isAnyWorkerOutdated
}
